import { useTranslation } from 'react-i18next';

import type { PearlChainUpdateType } from '@/types';

import useGetJourneyData from '@/hooks/use-get-journey-data';
import useGetPearlChain from '@/hooks/use-get-pearl-chain';
import useGetRoutesDeviationMap from '@/hooks/use-get-routes-deviation-map';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import getSecondStop from '@/utils/helpers/get-second-stop';

const useGetTrainDetails = (paramLockedId: string | null, options: { enabled?: boolean } = {}) => {
  const { t } = useTranslation();

  const isEnabled = options.enabled ?? true;

  const { data } = useGetJourneyData(paramLockedId!, { enabled: isEnabled });

  const { data: pearlChainData, isLoading: isPearlChainLoading } =
    useSocketSubscription<PearlChainUpdateType>('pearl_chain', {
      params: { journey_id: paramLockedId },
      enabled: isEnabled,
      filter: (incoming) => incoming.journeyId === paramLockedId,
    });

  const { passedStops, remainingStops, isTrainMoving, hasJourneyEnded } = useGetPearlChain(
    [],
    pearlChainData,
    data?.journeyDate,
    paramLockedId!
  );

  const { lineNumber, journeyNumber, journeyCategory, journeyType } = data || {};

  const { deviationMap, isLoading: isRoutesDeviationMapLoading } = useGetRoutesDeviationMap();

  const deviation = paramLockedId ? deviationMap[paramLockedId] : 0;

  if (!isEnabled || !data) {
    return { data: null };
  }

  const firstStop =
    isTrainMoving || hasJourneyEnded ? passedStops[passedStops.length - 1] : remainingStops[0];
  const filteredPasStops = remainingStops.filter(
    ({ stoppingState }) => stoppingState !== 'never_stop'
  );

  const secondStop = getSecondStop(filteredPasStops, remainingStops, isTrainMoving);

  return {
    data: {
      lineNumber: lineNumber,
      journeyNumber: journeyNumber,
      deviation: deviation,
      currentStation: firstStop?.stop?.designationOfficial,
      nextStation: !hasJourneyEnded
        ? secondStop?.stop?.designationOfficial
        : t('common.journey_ended'),
      journeyCategory,
      journeyType,
    },
    isTrainInfoLoading: isRoutesDeviationMapLoading || isPearlChainLoading,
  };
};

export default useGetTrainDetails;
